import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, ArrowUpRight, Landmark, ScanLine, ShieldCheck, Sparkles, GitBranch,
  Fingerprint, Banknote, Lock, Gauge, Users, Search, Crown, Briefcase, TrendingUp,
  FileCheck2, Database, AlertTriangle, Leaf, Download, Calculator, CheckCircle2, Quote, X,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { PERSONA_DETAILS } from "./whyLestariPersonaData";

const API = process.env.REACT_APP_BACKEND_URL;
const COLLATERAL = { volume: "Vol 1.0", published: "Published August 2026" };

/* ------------------------------ static content ------------------------------ */
const DRIVERS = [
  { year: "NOW", title: "SGX climate reporting", body: "SGX-listed issuers must make climate-related disclosures aligned to the ISSB baseline. Large non-listed companies follow on a phased timeline — the reporting perimeter is widening, not shrinking." },
  { year: "NOW", title: "IFRS S1 / S2 baseline", body: "Investors and regulators are converging on one global sustainability-disclosure language. Preparing data to that standard once beats re-formatting spreadsheets for every request." },
  { year: "2026", title: "Carbon tax escalation", body: "Singapore's Carbon Pricing Act rate rises from S$25 to S$45/tCO2e in 2026–27, with S$50–80 signalled by 2030. Carbon becomes a real line item on the P&L." },
  { year: "NOW", title: "EU CBAM & supply-chain demands", body: "Exporters of steel, aluminium and cement articles face EU carbon-border data requests, while banks, tenders and multinational customers push ESG questionnaires down to Singapore SMEs." },
  { year: "NOW", title: "Green financing advantage", body: "Grants (EDG, EEG, SRG, EFS-Green) and bank green loans reward companies that can evidence their ESG data. Poor data is now a cost of capital problem." },
];

const VALUE = [
  { icon: Sparkles, stat: "Hours → minutes", label: "Data capture", body: "Drop an ERP ledger, utility bill or HR export — AI classifies rows into the ESG taxonomy, suggests Singapore emission factors, flags duplicates and remembers the file format for next month." },
  { icon: Fingerprint, stat: "Every figure", label: "Traceable", body: "Click any number and drill to who captured it, from which document, through which formula and factor — audit-grade lineage without the email trail." },
  { icon: Banknote, stat: "S$ financing", label: "Unlocked", body: "Explainable grant eligibility, bank green-loan matching, readiness scoring and lender-ready application packs turn measured ESG data into cheaper capital." },
  { icon: Gauge, stat: "One screen", label: "Board oversight", body: "Targets, glide paths, net-zero roadmap, scenario stress tests and carbon-cost projections — decision-ready, not spreadsheet archaeology." },
  { icon: AlertTriangle, stat: "Before disclosure", label: "Errors caught", body: "4-layer anomaly detection and an AI reconciliation agent surface duplicates, contradictions and gaps across every data source before they reach a report." },
  { icon: FileCheck2, stat: "8+ frameworks", label: "One dataset", body: "SGX Core Metrics, IFRS S1/S2, GRI, TCFD, SASB, CDP, EU CSRD/ESRS and UN SDGs mapped from a single source of truth — including iXBRL/ESEF filing packs." },
];

const DIFFERENTIATORS = [
  { icon: Sparkles, title: "AI with human guardrails", body: "Every AI draft is labelled and requires human review before use. A full AI-use transparency log records every model call, cost and confidence — defensible to auditors." },
  { icon: Landmark, title: "Singapore-first by design", body: "SEFR emission factors (EMA grid factor), Singpass/Corppass + ACRA onboarding, Carbon Pricing Act module, SGX obligations and the local grant catalogue — not a generic global tool localised as an afterthought." },
  { icon: Fingerprint, title: "Audit-grade lineage", body: "Immutable audit trails, per-record provenance, emission-factor sign-off governance and assurance evidence packs. Built for IFRS S2 assurance, not just dashboards." },
  { icon: Leaf, title: "Measurement to money", body: "The only flow that runs end-to-end: capture → verify → disclose → finance. Grants, green loans and carbon economics live in the same system as your data." },
  { icon: Lock, title: "Enterprise-grade tenancy", body: "Strict multi-tenant isolation, role-based access for 8 personas, TOTP MFA with org-wide policy, and a platform operations console kept separate from tenant data." },
  { icon: GitBranch, title: "Evolves with regulation", body: "Grant terms refresh from official sources with human-reviewed diffs; factor libraries are versioned; the platform ships regulatory updates continuously." },
];

const PERSONAS = [
  { id: "sme-founder", icon: Briefcase, who: "SME Founder", need: "Win tenders & bank facilities without hiring an ESG team" },
  { id: "esg-manager", icon: TrendingUp, who: "ESG Manager", need: "One control tower instead of forty spreadsheets" },
  { id: "cfo", icon: Banknote, who: "CFO", need: "Carbon as a managed cost — tax, ICP, grants, green loans" },
  { id: "board", icon: Crown, who: "Board", need: "Decision-ready oversight, IFRS S2-aligned" },
  { id: "auditor", icon: Search, who: "Auditor", need: "Every figure traceable to source in two clicks" },
];

const GROUP_ORDER = ["Core", "Data & AI", "Reporting & Compliance", "Climate & Risk", "Finance", "Platform & Support"];
const GROUP_SHOTS = {
  "Core": { src: "/showcase/core.jpg", caption: "Control Tower — live emissions, targets & data quality" },
  "Data & AI": { src: "/showcase/data-ai.jpg", caption: "Data Management — AI import, lineage & reconciliation" },
  "Reporting & Compliance": { src: "/showcase/reporting.jpg", caption: "ESG Scorecard — framework-aligned KPIs & RAG status" },
  "Climate & Risk": { src: "/showcase/climate-risk.jpg", caption: "Risk Register — exposure, heatmap & mitigations" },
  "Finance": { src: "/showcase/finance.jpg", caption: "Green Financing — readiness, grants & bank cases" },
  "Platform & Support": { src: "/showcase/platform.jpg", caption: "Connections — ERP, HRMS & regulator connectors" },
};
const GROUP_BLURB = {
  "Core": "The foundation every plan starts with.",
  "Data & AI": "Where months of manual work disappear.",
  "Reporting & Compliance": "From data to defensible disclosure.",
  "Climate & Risk": "Quantify what climate does to the business.",
  "Finance": "Turn ESG performance into capital.",
  "Platform & Support": "Enterprise controls and service.",
};

/* ------------------------------ small components ------------------------------ */
function PlanBadge({ f, prices }) {
  const t = f.tiers || {};
  let label, cls;
  if (t.lite) { label = `All plans · from S$${prices.lite}`; cls = "border-[#262831] text-neutral-300 bg-[#15171C]"; }
  else if (t.growth) { label = `Growth+ · S$${prices.growth}`; cls = "border-neutral-600 text-white bg-neutral-800"; }
  else { label = `Control · S$${prices.control}`; cls = "border-[#D4FF00]/30 text-[#D4FF00] bg-[#D4FF00]/10"; }
  return (
    <div className="flex flex-col items-end gap-1 shrink-0" data-testid={`wl-badge-${f.key}`}>
      <span className={`font-mono text-[10px] px-2 py-0.5 rounded-sm border ${cls}`}>{label}</span>
      {f.addon && f.addon_price_sgd != null && (
        <span className="font-mono text-[10px] px-2 py-0.5 rounded-sm border border-orange-500/30 text-orange-400 bg-orange-500/10">
          Add-on S${f.addon_price_sgd}/mo
        </span>
      )}
    </div>
  );
}

function SectionHead({ num, eyebrow, title, sub }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-12">
      <div className="lg:col-span-3">
        <span className="font-mono text-5xl text-[#262831] select-none">{num}</span>
      </div>
      <div className="lg:col-span-9">
        <div className="label-eyebrow mb-2 text-[#D4FF00]">{eyebrow}</div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter leading-none">{title}</h2>
        {sub && <p className="text-sm text-neutral-400 mt-3 max-w-2xl leading-relaxed">{sub}</p>}
      </div>
    </div>
  );
}

function HeroMatrix() {
  const lit = new Set([3, 8, 13, 17, 22, 26, 31, 36, 41, 44, 49, 54, 58, 63]);
  return (
    <div className="grid grid-cols-8 gap-px bg-[#262831] border border-[#262831] rounded-sm overflow-hidden" aria-hidden="true">
      {Array.from({ length: 64 }).map((_, i) => (
        <div key={i} className={`aspect-square ${lit.has(i) ? "bg-[#D4FF00]/80 wl-pulse" : "bg-[#0C0D10]"}`}
          style={lit.has(i) ? { animationDelay: `${(i % 9) * 0.35}s` } : undefined} />
      ))}
    </div>
  );
}

function RoiCalculator() {
  const [employees, setEmployees] = useState(50);
  const [emissions, setEmissions] = useState(1500);
  const manual = Math.max(120, Math.round(employees * 2.5));
  const saved = Math.round(manual * 0.7);
  const savedSgd = saved * 65;
  const taxable = emissions >= 25000;
  const fmt = (n) => n.toLocaleString("en-SG");
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="wl-roi-calculator">
      <div className="lg:col-span-4 border border-[#262831] rounded-sm bg-[#15171C] p-6">
        <div className="label-eyebrow text-[10px] mb-5 text-neutral-500">Your organisation</div>
        <label className="block mb-5">
          <span className="text-sm text-neutral-300">Employees</span>
          <input type="number" min="1" max="100000" value={employees} data-testid="wl-roi-employees"
            onChange={(e) => setEmployees(Math.max(1, Number(e.target.value) || 0))}
            className="mt-2 w-full bg-[#0C0D10] border border-[#262831] rounded-sm px-3 py-2 font-mono text-lg focus:border-[#D4FF00]/50 focus:outline-none transition-colors" />
          <input type="range" min="5" max="2000" step="5" value={Math.min(employees, 2000)}
            onChange={(e) => setEmployees(Number(e.target.value))} className="w-full mt-2 accent-[#D4FF00]" data-testid="wl-roi-employees-slider" />
        </label>
        <label className="block">
          <span className="text-sm text-neutral-300">Annual emissions (tCO2e, Scope 1+2)</span>
          <input type="number" min="0" max="10000000" value={emissions} data-testid="wl-roi-emissions"
            onChange={(e) => setEmissions(Math.max(0, Number(e.target.value) || 0))}
            className="mt-2 w-full bg-[#0C0D10] border border-[#262831] rounded-sm px-3 py-2 font-mono text-lg focus:border-[#D4FF00]/50 focus:outline-none transition-colors" />
          <input type="range" min="100" max="50000" step="100" value={Math.min(emissions, 50000)}
            onChange={(e) => setEmissions(Number(e.target.value))} className="w-full mt-2 accent-[#D4FF00]" data-testid="wl-roi-emissions-slider" />
        </label>
        <p className="font-mono text-[10px] text-neutral-600 mt-5 leading-relaxed">
          Assumptions: ~2.5h of manual ESG data wrangling per employee per year (min 120h) · 70% automated away by
          AI import & mapping · S$65/h loaded staff cost. Indicative, not a quotation.
        </p>
      </div>
      <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 border-t border-l border-[#262831]">
        <div className="border-r border-b border-[#262831] p-6">
          <div className="label-eyebrow text-[10px] text-neutral-500 mb-2">Time back per year</div>
          <div className="font-mono text-4xl font-bold text-[#D4FF00]" data-testid="wl-roi-hours">{fmt(saved)} h</div>
          <p className="text-xs text-neutral-500 mt-2">of the ~{fmt(manual)}h your team likely spends collecting, cleaning and consolidating ESG data manually.</p>
        </div>
        <div className="border-r border-b border-[#262831] p-6">
          <div className="label-eyebrow text-[10px] text-neutral-500 mb-2">Value of time saved</div>
          <div className="font-mono text-4xl font-bold" data-testid="wl-roi-cost">S${fmt(savedSgd)}</div>
          <p className="text-xs text-neutral-500 mt-2">per year at S$65/h — before counting avoided consultant fees and restatement risk.</p>
        </div>
        <div className="border-r border-b border-[#262831] p-6">
          <div className="label-eyebrow text-[10px] text-neutral-500 mb-2">Carbon cost @ S$45/t (2026)</div>
          <div className="font-mono text-4xl font-bold text-orange-400" data-testid="wl-roi-tax-2026">S${fmt(emissions * 45)}</div>
          <p className="text-xs text-neutral-500 mt-2" data-testid="wl-roi-threshold-note">
            {taxable
              ? "Above the 25,000 tCO2e threshold — this is statutory Carbon Pricing Act liability."
              : "Below the 25,000 tCO2e statutory threshold — but this is what your carbon costs at a shadow price, and what customers and banks increasingly price in."}
          </p>
        </div>
        <div className="border-r border-b border-[#262831] p-6">
          <div className="label-eyebrow text-[10px] text-neutral-500 mb-2">By 2030 @ S$50–80/t</div>
          <div className="font-mono text-4xl font-bold text-orange-400" data-testid="wl-roi-tax-2030">S${fmt(emissions * 50)}–{fmt(emissions * 80)}</div>
          <p className="text-xs text-neutral-500 mt-2">Every tonne abated now avoids this recurring cost — and strengthens grant and green-loan eligibility.</p>
        </div>
      </div>
    </div>
  );
}


function PersonaSheet({ personaId, onClose }) {
  const p = personaId ? PERSONA_DETAILS[personaId] : null;
  return (
    <Sheet open={!!p} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl bg-[#0C0D10] border-[#262831] text-white overflow-y-auto p-0" data-testid="wl-persona-sheet">
        {p && (
          <div>
            <div className="p-6 border-b border-[#262831] bg-[#15171C]">
              <div className="flex items-start gap-4">
                <img src={p.img} alt={p.person} className="h-24 w-24 rounded-sm object-cover border border-[#262831] shrink-0" data-testid="wl-persona-sheet-img" />
                <div className="min-w-0">
                  <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-sm border border-amber-500/30 text-amber-500/90 bg-amber-500/10 uppercase tracking-wider">Fictional example</span>
                  <SheetTitle className="font-display text-2xl font-bold tracking-tight text-white mt-2" data-testid="wl-persona-sheet-name">{p.person}</SheetTitle>
                  <div className="text-sm text-neutral-400 mt-0.5">{p.role}</div>
                  <div className="font-mono text-xs text-[#D4FF00] mt-1">{p.company}</div>
                </div>
              </div>
              <p className="text-sm text-neutral-300 mt-4 italic">"{p.tagline}"</p>
            </div>

            <div className="p-6 space-y-8">
              <div>
                <div className="label-eyebrow text-[10px] text-neutral-500 mb-3">Company profile (mock)</div>
                <div className="grid grid-cols-1 gap-2">
                  {p.profile.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[90px_1fr] gap-3 text-xs">
                      <span className="font-mono text-neutral-500">{k}</span>
                      <span className="text-neutral-300">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="label-eyebrow text-[10px] text-neutral-500 mb-3">The before</div>
                <div className="border-l-2 border-orange-500/40 pl-4 text-sm text-neutral-400 leading-relaxed" data-testid="wl-persona-before">{p.before}</div>
              </div>

              <div>
                <div className="label-eyebrow text-[10px] text-neutral-500 mb-4">What changes with Lestari</div>
                <div className="space-y-5">
                  {p.value.map((v, i) => (
                    <div key={i} className="flex items-start gap-3" data-testid={`wl-persona-value-${i}`}>
                      <div className="h-6 w-6 rounded-sm bg-[#D4FF00]/10 border border-[#D4FF00]/20 flex items-center justify-center shrink-0 font-mono text-[11px] text-[#D4FF00] mt-0.5">{i + 1}</div>
                      <div>
                        <div className="text-sm font-medium">{v.title}</div>
                        <p className="text-xs text-neutral-400 mt-1 leading-relaxed">{v.body}</p>
                        <div className="font-mono text-[10px] text-neutral-600 mt-1.5">{v.features}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 border-t border-l border-[#262831]" data-testid="wl-persona-metrics">
                {p.metrics.map(([stat, label]) => (
                  <div key={label} className="border-r border-b border-[#262831] p-3">
                    <div className="font-mono text-sm font-bold text-[#D4FF00] leading-tight">{stat}</div>
                    <div className="text-[10px] text-neutral-500 mt-1 leading-snug">{label}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-sm border border-[#D4FF00]/20 bg-[#D4FF00]/[0.04] p-4" data-testid="wl-persona-career">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-3.5 w-3.5 text-[#D4FF00]" />
                  <span className="label-eyebrow text-[10px] text-[#D4FF00]">Sustainability as career & strategic capital</span>
                </div>
                <p className="text-sm text-neutral-300 leading-relaxed">{p.career}</p>
              </div>

              <Link to="/get-started" data-testid="wl-persona-cta"
                className="inline-flex items-center gap-2 bg-[#D4FF00] text-black px-5 py-2.5 rounded-sm font-semibold hover:bg-[#bce600] transition-colors">
                Start where {p.firstName} did — free trial <ArrowRight className="h-4 w-4" />
              </Link>
              <p className="font-mono text-[10px] text-neutral-600 -mt-4">
                All persons, companies and figures above are fictional and illustrative only.
              </p>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------ page ------------------------------ */
export default function WhyLestari() {
  const [data, setData] = useState({ packages: [], features: [] });
  const [build, setBuild] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [activePersona, setActivePersona] = useState(null);

  const downloadBrochure = async () => {
    setPdfBusy(true);
    try {
      const r = await axios.get(`${API}/api/showcase/brochure.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const el = document.createElement("a");
      el.href = url; el.download = "lestari_capability_brochure.pdf"; el.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ } finally { setPdfBusy(false); }
  };

  useEffect(() => {
    axios.get(`${API}/api/showcase`).then((r) => setData(r.data)).catch(() => {});
    axios.get(`${API}/api/build-info`).then((r) => setBuild(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const prices = Object.fromEntries((data.packages || []).map((p) => [p.code, p.price_sgd]));
  const groups = GROUP_ORDER.filter((g) => (data.features || []).some((f) => f.group === g));

  return (
    <div className="min-h-screen bg-[#0C0D10] text-white" data-testid="why-lestari-page">
      <style>{`
        @keyframes wlPulse { 0%,100% { opacity:.25 } 50% { opacity:1 } }
        .wl-pulse { animation: wlPulse 3.2s ease-in-out infinite; }
        @keyframes wlRise { from { opacity:0; transform:translateY(14px) } to { opacity:1; transform:translateY(0) } }
        .wl-rise { animation: wlRise .7s ease-out both; }
        @keyframes wlBlink { 0%,49% { opacity:1 } 50%,100% { opacity:0 } }
        .wl-cursor { animation: wlBlink 1.1s step-end infinite; }
      `}</style>

      {/* top bar */}
      <header className="border-b border-[#262831]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-2" data-testid="wl-logo">
            <span className="h-7 w-7 rounded-sm bg-[#D4FF00] flex items-center justify-center font-mono text-black text-xs font-bold">&lt;/&gt;</span>
            <span className="font-display font-bold tracking-tight">LESTARI</span>
            <span className="label-eyebrow text-[9px] text-neutral-500 hidden sm:inline">ESG Control Tower</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/about" className="text-neutral-400 hover:text-white transition-colors" data-testid="wl-nav-about">About</Link>
            <Link to="/pricing" className="text-neutral-400 hover:text-white transition-colors" data-testid="wl-nav-pricing">Pricing</Link>
            <Link to="/login" className="text-neutral-400 hover:text-white transition-colors" data-testid="wl-nav-login">Sign in</Link>
            <Link to="/get-started" data-testid="wl-nav-trial"
              className="bg-[#D4FF00] text-black px-3.5 py-1.5 rounded-sm text-sm font-semibold hover:bg-[#bce600] transition-colors">
              Start free trial
            </Link>
          </nav>
        </div>
      </header>

      {/* 01 · hero */}
      <section className="border-b border-[#262831]" data-testid="wl-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 wl-rise">
            <div className="label-eyebrow mb-4 text-[#D4FF00]">ESG Capability Brochure · Singapore Edition</div>
            <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tighter leading-none">
              The AI-powered ESG control tower<span className="text-[#D4FF00]"> built for Singapore.</span>
            </h1>
            <p className="text-neutral-400 mt-6 max-w-xl leading-relaxed">
              Lestari takes a company from scattered spreadsheets to assurance-ready disclosure and green
              financing — data capture, GHG accounting, materiality, reporting, climate risk and carbon
              economics in one governed platform.
            </p>
            <div className="flex items-center gap-3 mt-8 flex-wrap">
              <Link to="/get-started" data-testid="wl-cta-trial"
                className="inline-flex items-center gap-2 bg-[#D4FF00] text-black px-5 py-2.5 rounded-sm font-semibold hover:bg-[#bce600] transition-colors">
                Start a 15-day free trial <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#pricing" data-testid="wl-cta-pricing"
                className="inline-flex items-center gap-2 border border-[#262831] px-5 py-2.5 rounded-sm text-sm text-neutral-300 hover:text-white hover:border-neutral-600 transition-colors">
                See plans & pricing <ArrowUpRight className="h-4 w-4" />
              </a>
              <button onClick={downloadBrochure} disabled={pdfBusy} data-testid="wl-download-brochure"
                className="inline-flex items-center gap-2 border border-[#262831] px-5 py-2.5 rounded-sm text-sm text-neutral-300 hover:text-white hover:border-neutral-600 transition-colors disabled:opacity-50">
                <Download className="h-4 w-4" /> {pdfBusy ? "Preparing…" : "Brochure (PDF)"}
              </button>
            </div>
            <div className="flex items-center gap-6 mt-10 text-[11px] font-mono text-neutral-500 flex-wrap">
              <span>SGX · IFRS S1/S2 · GRI · TCFD</span><span>GHG Protocol S1·S2·S3</span><span>Carbon Pricing Act</span>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block wl-rise" style={{ animationDelay: ".2s" }}>
            <HeroMatrix />
            <div className="font-mono text-[10px] text-neutral-600 mt-2 text-right">fig. 01 — one data spine, every disclosure</div>
          </div>
        </div>
      </section>

      {/* 02 · why now */}
      <section className="border-b border-[#262831]" data-testid="wl-why-now">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <SectionHead num="02" eyebrow="Why now" title="Regulation has moved. Have your spreadsheets?"
            sub="Five forces make structured ESG data unavoidable for Singapore companies — listed or not." />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 border-l border-[#262831]">
              {DRIVERS.map((d, i) => (
                <div key={d.title} className="pl-6 pb-8 relative" data-testid={`wl-driver-${i}`}>
                  <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-sm bg-[#D4FF00]" />
                  <div className="font-mono text-[10px] text-[#D4FF00] mb-1">{d.year}</div>
                  <div className="font-display font-semibold text-lg tracking-tight">{d.title}</div>
                  <p className="text-sm text-neutral-400 mt-1 leading-relaxed max-w-xl">{d.body}</p>
                </div>
              ))}
            </div>
            <div className="lg:col-span-5">
              <div className="border border-[#262831] rounded-sm bg-[#15171C] p-5 sticky top-6" data-testid="wl-tax-ticker">
                <div className="flex items-center gap-2 label-eyebrow text-[10px] mb-4"><ScanLine className="h-3.5 w-3.5 text-[#D4FF00]" /> Carbon tax trajectory · SG CPA</div>
                <div className="font-mono text-sm space-y-2.5">
                  <div className="flex justify-between"><span className="text-neutral-500">2024–25</span><span>S$25 / tCO2e</span></div>
                  <div className="flex justify-between text-[#D4FF00]"><span>2026–27</span><span>S$45 / tCO2e</span></div>
                  <div className="flex justify-between text-orange-400"><span>by 2030</span><span>S$50–80 / tCO2e</span></div>
                  <div className="border-t border-[#262831] pt-2.5 flex justify-between text-neutral-500 text-xs">
                    <span>Δ 2024→2026</span><span className="text-white">+80%<span className="wl-cursor text-[#D4FF00]">_</span></span>
                  </div>
                </div>
                <p className="text-[11px] text-neutral-500 mt-4 leading-relaxed">
                  Even below the 25,000 tCO2e taxable threshold, customers, banks and tenders increasingly price
                  your carbon for you. Measuring it first is the only way to manage it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 03 · business value */}
      <section className="border-b border-[#262831]" data-testid="wl-value">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <SectionHead num="03" eyebrow="Business value" title="What the platform actually changes"
            sub="Not dashboards for their own sake — measurable operational and financial outcomes." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#262831]" data-testid="wl-value-grid">
            {VALUE.map((v) => (
              <div key={v.label} className="border-r border-b border-[#262831] p-6 hover:bg-[#15171C] transition-colors group">
                <v.icon className="h-5 w-5 text-[#D4FF00] mb-4" />
                <div className="font-mono text-xl font-bold tracking-tight">{v.stat}</div>
                <div className="label-eyebrow text-[10px] mt-1 mb-3 text-neutral-500 group-hover:text-neutral-400 transition-colors">{v.label}</div>
                <p className="text-sm text-neutral-400 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 04 · ROI calculator */}
      <section className="border-b border-[#262831]" data-testid="wl-roi">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <SectionHead num="04" eyebrow="Run your own numbers" title="What is ESG friction costing you?"
            sub="Two inputs, live answers — estimated time returned to your team and your carbon-cost exposure on the Carbon Pricing Act trajectory." />
          <RoiCalculator />
        </div>
      </section>

      {/* 05 · feature ledger */}
      <section className="border-b border-[#262831]" data-testid="wl-feature-ledger">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <SectionHead num="05" eyebrow="The capability catalogue" title="Every major feature, and where it lives"
            sub="Live from the platform's plan catalogue. Features included in a lower tier are included in all higher tiers; 'Add-on' marks pay-as-you-go availability for plans below the inclusion tier." />
          <div className="space-y-14">
            {groups.map((g) => (
              <div key={g} className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid={`wl-group-${g.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
                <div className="lg:col-span-3">
                  <div className="lg:sticky lg:top-6">
                    <div className="font-display text-xl font-bold tracking-tight">{g}</div>
                    <p className="text-xs text-neutral-500 mt-1">{GROUP_BLURB[g]}</p>
                    {GROUP_SHOTS[g] && (
                      <figure className="mt-4 hidden lg:block" data-testid={`wl-shot-${g.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
                        <img src={GROUP_SHOTS[g].src} alt={GROUP_SHOTS[g].caption} loading="lazy"
                          onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                          className="w-full border border-[#262831] rounded-sm hover:border-[#D4FF00]/40 transition-colors" />
                        <figcaption className="font-mono text-[10px] text-neutral-600 mt-1.5 leading-snug">{GROUP_SHOTS[g].caption}</figcaption>
                      </figure>
                    )}
                  </div>
                </div>
                <div className="lg:col-span-9 border-t border-[#262831]">
                  {(data.features || []).filter((f) => f.group === g).map((f) => (
                    <div key={f.key} className="flex items-start gap-4 py-4 border-b border-[#262831] hover:bg-[#15171C] transition-colors px-2 -mx-2"
                      data-testid={`wl-feature-${f.key}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm">{f.name}</div>
                        <p className="text-xs text-neutral-500 mt-0.5 leading-relaxed">{f.description}</p>
                      </div>
                      <div className="font-mono text-[10px] text-neutral-600 hidden md:block w-32 shrink-0 pt-0.5">{f.module || ""}</div>
                      <PlanBadge f={f} prices={prices} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 06 · differentiators */}
      <section className="border-b border-[#262831]" data-testid="wl-differentiators">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <SectionHead num="06" eyebrow="Key differentiators" title="Why Lestari, not another dashboard" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {DIFFERENTIATORS.map((d) => (
              <div key={d.title} className="border border-[#262831] rounded-sm p-6 bg-[#15171C] hover:-translate-y-1 transition-transform" data-testid={`wl-diff-${d.title.split(" ")[0].toLowerCase()}`}>
                <d.icon className="h-5 w-5 text-[#D4FF00] mb-4" />
                <div className="font-display font-semibold tracking-tight">{d.title}</div>
                <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 07 · pricing */}
      <section id="pricing" className="border-b border-[#262831] scroll-mt-6" data-testid="wl-pricing">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <SectionHead num="07" eyebrow="Plans" title="Priced for the journey, not the enterprise budget"
            sub="Per active user, per Singapore legal entity, per month (SGD). Annual billing earns 2 months free. Features above a plan's tier are available pay-as-you-go." />
          <div className="grid grid-cols-1 md:grid-cols-3 border border-[#262831] rounded-sm overflow-hidden">
            {(data.packages || []).map((p) => (
              <div key={p.code} className={`p-8 border-b md:border-b-0 md:border-r last:border-r-0 border-[#262831] ${p.code === "control" ? "bg-[#D4FF00]/5" : ""}`}
                data-testid={`wl-plan-${p.code}`}>
                <div className="label-eyebrow text-[10px] mb-2 text-neutral-500">{p.tagline}</div>
                <div className="font-display text-2xl font-bold tracking-tight">{p.name}</div>
                <div className="font-mono text-4xl font-bold mt-4">S${p.price_sgd}<span className="text-sm text-neutral-500 font-normal"> /user/mo</span></div>
                <div className="font-mono text-[10px] text-neutral-600 mt-1">{p.price_unit}</div>
                <p className="text-sm text-neutral-400 mt-4 leading-relaxed">{p.best_for}</p>
                <Link to="/get-started" data-testid={`wl-plan-cta-${p.code}`}
                  className={`inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-sm text-sm font-semibold transition-colors ${p.code === "control" ? "bg-[#D4FF00] text-black hover:bg-[#bce600]" : "border border-[#262831] text-neutral-300 hover:text-white hover:border-neutral-600"}`}>
                  Start with {p.name.replace("Lestari ", "")} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            ))}
          </div>
          <p className="font-mono text-[11px] text-neutral-500 mt-4" data-testid="wl-addon-note">
            + Pay-as-you-go add-ons per entity/month — e.g. Green Financing S$35, TCFD Scenarios S$40, CBAM S$30, Carbon Tax & ICP S$28, SGX Filing Pack S$40. Every trial starts with 15 days free.
          </p>
        </div>
      </section>

      {/* 07 · personas */}
      <section className="border-b border-[#262831]" data-testid="wl-personas">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="label-eyebrow text-[10px] mb-2 text-neutral-500">Built for the people who carry the ESG mandate</div>
          <p className="text-xs text-neutral-600 mb-6 font-mono">Click a role to meet a (fictional) peer — from a 42-person SME to an SGX-listed group.</p>
          <div className="grid grid-cols-2 md:grid-cols-5 border-t border-l border-[#262831]">
            {PERSONAS.map((p) => (
              <button key={p.who} onClick={() => setActivePersona(p.id)}
                data-testid={`wl-persona-${p.who.replace(/\s+/g, "-").toLowerCase()}`}
                className="border-r border-b border-[#262831] p-5 text-left group hover:bg-[#15171C] transition-colors cursor-pointer">
                <div className="flex items-center justify-between mb-3">
                  <img src={PERSONA_DETAILS[p.id].img} alt={p.who} loading="lazy"
                    className="h-14 w-14 rounded-sm object-cover border border-[#262831] group-hover:border-[#D4FF00]/40 transition-colors" />
                  <ArrowUpRight className="h-4 w-4 text-neutral-700 group-hover:text-[#D4FF00] transition-colors" />
                </div>
                <div className="font-display font-semibold text-sm flex items-center gap-1.5"><p.icon className="h-3.5 w-3.5 text-[#D4FF00]" /> {p.who}</div>
                <p className="text-[11px] text-neutral-500 mt-1 leading-snug">{p.need}</p>
                <div className="font-mono text-[10px] text-neutral-600 mt-2 group-hover:text-neutral-400 transition-colors">
                  {PERSONA_DETAILS[p.id].person} · {PERSONA_DETAILS[p.id].company.split(" ").slice(0, 2).join(" ")}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <PersonaSheet personaId={activePersona} onClose={() => setActivePersona(null)} />

      {/* 08 · CTA */}
      <section data-testid="wl-final-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 text-center">
          <div className="label-eyebrow mb-4 text-[#D4FF00]">The bottom line</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-none max-w-3xl mx-auto">
            ESG data is now a balance-sheet asset. Start building yours.
          </h2>
          <p className="text-neutral-400 mt-5 max-w-xl mx-auto leading-relaxed">
            Verified onboarding with Singpass/Corppass and ACRA pre-fill takes minutes. Fifteen days free, no card required.
          </p>
          <Link to="/get-started" data-testid="wl-cta-bottom"
            className="inline-flex items-center gap-2 bg-[#D4FF00] text-black px-6 py-3 rounded-sm font-semibold mt-8 hover:bg-[#bce600] transition-colors">
            Get started free <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="flex items-center justify-center gap-2 mt-6 text-[11px] font-mono text-neutral-600">
            <ShieldCheck className="h-3.5 w-3.5" /> Strict tenant isolation · MFA · Immutable audit trails
          </div>
        </div>
      </section>

      {/* footer */}
      <footer className="border-t border-[#262831]" data-testid="wl-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] font-mono text-neutral-500">
          <a href="https://sustek.ai" target="_blank" rel="noopener noreferrer" className="hover:text-neutral-300 transition-colors" data-testid="wl-footer-copyright">
            Copyright © SusTek Pte Ltd. All Rights Reserved.
          </a>
          <Link to="/about" className="hover:text-neutral-300 transition-colors" data-testid="wl-footer-about">About Lestari</Link>
          <span data-testid="wl-footer-collateral">{COLLATERAL.volume} · {COLLATERAL.published}</span>
          <span data-testid="wl-footer-build">
            {build ? `Platform ${build.version} · build ${build.build_number} · git ${build.git_sha} · ${new Date(build.deploy_timestamp).toLocaleDateString("en-SG", { day: "2-digit", month: "short", year: "numeric" })}` : "Platform build —"}
          </span>
        </div>
      </footer>
    </div>
  );
}
